import React, { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  useTheme,
  Snackbar,
  Alert,
  InputAdornment,
  Divider,
  Link as MUILink,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import SortlyBooksLogo from "../components/SortlyBooksLogo";

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const theme = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");
    setError("");

    try {
      await sendPasswordResetEmail(auth, email);
      setStatus(
        "If an account exists for this email, a reset link has been sent."
      );
    } catch (err) {
      console.warn("Reset error:", err.message);
      setError("We couldn't send a reset link. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f9fbff",
        px: 2,
      }}
    >
      <Navbar />

      <Box textAlign="center">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <SortlyBooksLogo />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 4,
              maxWidth: 400,
              borderRadius: "16px",
              boxShadow: "0 6px 30px rgba(0,0,0,0.05)",
              backgroundColor: "white",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Reset Your Password
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your email and we’ll send a reset link.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                sx={{
                  backgroundColor: "#f1f5f9",
                  borderRadius: 2,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  borderRadius: "12px",
                  fontWeight: 600,
                  py: 1.2,
                  backgroundColor: "#1e3a8a",
                  "&:hover": { backgroundColor: "#1e40af" },
                }}
              >
                Send Reset Link
              </Button>

              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Back to{" "}
                <MUILink
                  component={Link}
                  to={`/login?email=${encodeURIComponent(email)}`}
                  underline="hover"
                >
                  Login
                </MUILink>{" "}
                or{" "}
                <MUILink component={Link} to="/signup" underline="hover">
                  Create an account
                </MUILink>
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography variant="caption" color="text.secondary">
                If you don’t receive an email in a few minutes, check your spam
                or try again.
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={!!status}
        autoHideDuration={4000}
        onClose={() => setStatus("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setStatus("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {status}
        </Alert>
      </Snackbar>
    </Box>
  );
}
